import type {
  DATA_PROCESSING_STEP_TYPE,
  DEBT_REPORT_GROUP,
  DEBT_SERVICE_TYPE,
} from 'features/accounting/debts/types';
import { type REFUND_DATA_SERVICE_TYPE } from 'features/accounting/refund-data/types';
import { type SALES_DATA_SERVICE_TYPE } from 'features/accounting/sales-data/types';
import { type E_OFFICE_MANAGEMENT_TYPE } from 'features/content-template/types';
import { type INTERNAL_DOCUMENT_TYPE } from 'features/internal-document/common/types';
import { type WORK_TYPE } from 'features/work/types';
import {
  type ACCOUNTING_MANAGEMENT_TAB,
  type DIGITAL_SIGN_TAB,
  type SYSTEM_CATEGORY_TAB,
  type UTILITY_MANAGEMENT_TAB,
} from 'routes/types';

const publicPaths = {
  forbiddenPage: () => '/forbidden',
  loginPage: () => '/auth/login',
};

const privatePaths = {
  // ---------------------------------------------------------------------------
  // Module: Trang chủ (HOME)
  // ---------------------------------------------------------------------------

  /** Trang dashboard tổng quan — màn hình chính sau khi đăng nhập */
  dashboardPage: () => '/',

  /** Trang thông tin nội bộ — nhúng báo cáo Power BI */
  reportPage: () => `/report`,

  // ---------------------------------------------------------------------------
  // Module: Văn bản (DOCUMENT)
  // ---------------------------------------------------------------------------

  /** Danh sách văn bản nội bộ — phân loại theo type: văn bản đến / đi / phát hành điện tử */
  internalDocumentsListPage: (type: INTERNAL_DOCUMENT_TYPE) =>
    `/documents/internal/${type}`,

  /** Chi tiết một văn bản nội bộ — xem nội dung, lịch sử xử lý */
  internalDocumentDetailPage: (
    type: INTERNAL_DOCUMENT_TYPE,
    documentCode: string
  ) => `/documents/internal/${type}/${documentCode}`,

  // ---------------------------------------------------------------------------
  // Module: e-Office (Công việc & Đơn hành chính)
  // ---------------------------------------------------------------------------

  /** Danh sách công việc — lọc theo loại: tất cả / giao việc / nhận việc */
  workListPage: (type: WORK_TYPE) => `/e-office/work/list/${type}`,

  /** Chi tiết một công việc — xem tiến độ, bình luận, file đính kèm */
  workDetailPage: (workCode: string) => `/e-office/work/detail/${workCode}`,

  /** Danh sách đơn hành chính — nghỉ phép, công tác, tăng ca… */
  proposalListPage: () => `/e-office/proposal/list`,

  /** Chi tiết một đơn hành chính — xem trạng thái duyệt, lịch sử */
  proposalDetailPage: (proposalCode: string) =>
    `/e-office/proposal/detail/${proposalCode}`,

  /** Trang quản trị e-Office — cấu hình mẫu phê duyệt, template quy trình */
  eOfficeManagementPage: (pageType: E_OFFICE_MANAGEMENT_TYPE) =>
    `/e-office/management/${pageType}`,

  // ---------------------------------------------------------------------------
  // Module: Nhiệm vụ (TASK) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang danh sách Nhiệm vụ */
  taskListPage: () => `/task`,

  /** Trang quản trị quyền module Nhiệm vụ */
  taskManagementPermission: () => `/task-management-permission`,

  // ---------------------------------------------------------------------------
  // Module: Bảng lương (SALARY) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang quản trị quyền module Bảng lương */
  salaryManagementPermission: () => `/salary-management-permission`,

  // ---------------------------------------------------------------------------
  // Module: Kiểm soát rủi ro (RISK) — tạm ẩn, chưa triển khai
  // ---------------------------------------------------------------------------

  /** Trang quản trị quyền module Kiểm soát rủi ro */
  riskManagementPermission: () => `/risk-management-permission`,

  // ---------------------------------------------------------------------------
  // Module: Kế toán (ACCOUNTING)
  // ---------------------------------------------------------------------------

  /** Kế toán - Công nợ: Xử lý dữ liệu */
  accountingDebtDataProcessingPage: (
    processingType?: DATA_PROCESSING_STEP_TYPE | string
  ) => {
    if (processingType) {
      return `/accounting/debts/data-processing/${processingType}`;
    }
    return '/accounting/debts/data-processing';
  },

  /** Kế toán - Công nợ: Xử lý dữ liệu theo bước (import, reconciliation, processing) */
  accountingDebtDataProcessingStepPage: (
    processingType: DATA_PROCESSING_STEP_TYPE | string
  ) => {
    return `/accounting/debts/data-processing/${processingType}`;
  },

  /** Kế toán - Công nợ: Xử lý dữ liệu - Đối soát (Comparison) */
  accountingDebtDataProcessingComparisonPage: () => {
    return '/accounting/debts/data-processing/reconciliation';
  },

  /** Kế toán - Công nợ: Xử lý dữ liệu - Đối soát (Reconciliation) */
  accountingDebtDataProcessingReconciliationPage: (
    _serviceType?: DEBT_SERVICE_TYPE
  ) => {
    return '/accounting/debts/data-processing/reconciliation';
  },

  /** Kế toán - Công nợ: Dữ liệu treo */
  accountingDebtPendingDataPage: (serviceType?: DEBT_SERVICE_TYPE) => {
    if (serviceType) {
      return `/accounting/debts/pending/${serviceType}`;
    }
    return '/accounting/debts/pending';
  },

  /** Kế toán - Công nợ: Báo cáo */
  // eslint-disable-next-line max-params
  accountingDebtReportsPage: (
    group?: DEBT_REPORT_GROUP,
    reportKey?: string,
    subKey?: string
  ) => {
    if (group && reportKey && subKey) {
      return `/accounting/debts/reports/${group}/${reportKey}/${subKey}`;
    }
    if (group && reportKey) {
      return `/accounting/debts/reports/${group}/${reportKey}`;
    }
    if (group) {
      return `/accounting/debts/reports/${group}`;
    }
    return '/accounting/debts/reports';
  },

  /** Kế toán - Báo cáo giao dịch: Đối soát dữ liệu (Reconciliation) */
  accountingReceivableTransactionReportReconciliationPage: () =>
    '/accounting/debts/reports/receivables/transaction-report/reconciliation',

  /** Kế toán - Số dư đầu kỳ: Số dư đầu kỳ */
  accountingOpeningBalanceReceivablePage: () =>
    '/accounting/opening-balance/receivables',

  /** Kế toán - Số dư đầu kỳ: Số dư đầu kỳ NCC */
  accountingOpeningBalanceSupplierPage: () =>
    '/accounting/opening-balance/suppliers',

  /** Kế toán - Thuế: Yêu cầu hoá đơn */
  accountingTaxInvoiceRequestPage: () => '/accounting/tax/invoice-requests',

  /** Kế toán - Thuế: Hoá đơn xuất */
  accountingTaxIssuedInvoicePage: () => '/accounting/tax/issued-invoices',

  /** Kế toán - Tổng hợp công nợ: Tổng hợp công nợ khách hàng */
  accountingCustomerDebtSummaryPage: () => '/accounting/debt-summary/customers',

  /** Kế toán - Tổng hợp công nợ: Tổng hợp công nợ nhà cung cấp */
  accountingSupplierDebtSummaryPage: () => '/accounting/debt-summary/suppliers',

  /** Kế toán - Quản trị: Quản trị module Kế toán (Số dư đầu kỳ, Số dư đầu kỳ NCC) */
  accountingManagementPage: (tab?: ACCOUNTING_MANAGEMENT_TAB) =>
    `/accounting/management${tab ? `/${tab}` : ''}`,

  // ---------------------------------------------------------------------------
  // Legacy paths (kept for compatibility and redirects)
  // ---------------------------------------------------------------------------

  /** @deprecated Use accountingDebtDataProcessingPage instead */
  accountingSalesDataPage: (serviceType?: SALES_DATA_SERVICE_TYPE) =>
    serviceType
      ? `/accounting/data-processing/sales/${serviceType}`
      : '/accounting/data-processing/sales',

  /** @deprecated Use accountingDebtDataProcessingPage instead */
  accountingRefundDataPage: (serviceType?: REFUND_DATA_SERVICE_TYPE) =>
    serviceType
      ? `/accounting/data-processing/refunds/${serviceType}`
      : '/accounting/data-processing/refunds',

  /** @deprecated Kept for redirects */
  accountingSupplierDataPage: () => '/accounting/data-processing/suppliers',

  /** @deprecated Kept for redirects */
  accountingBankDataPage: () => '/accounting/data-processing/bank',

  /** @deprecated Kept for redirects */
  accountingCustomerPaymentDataPage: () =>
    '/accounting/data-processing/customer-payments',

  /** @deprecated Use accountingDebtReportsPage instead */
  accountingReceivableSalesReportPage: () =>
    '/accounting/receivables/sales-report',

  /** @deprecated Use accountingDebtReportsPage instead */
  accountingReceivableTransactionReportPage: () =>
    '/accounting/receivables/transaction-report',

  /** @deprecated Use accountingDebtReportsPage instead */
  accountingReceivableDebtReportPage: () =>
    '/accounting/receivables/debt-report',

  /** @deprecated Use accountingOpeningBalanceReceivablePage instead */
  accountingReceivableOpeningBalancePage: () =>
    '/accounting/receivables/opening-balance',

  /** @deprecated Use accountingDebtReportsPage instead */
  accountingPayableProviderTopupPage: () =>
    '/accounting/payables/provider-topup',

  /** @deprecated Use accountingDebtReportsPage instead */
  accountingPayableSupplierDebtReportPage: () =>
    '/accounting/debts/reports/payables/debt-report',

  /** @deprecated Use accountingOpeningBalanceSupplierPage instead */
  accountingPayableSupplierOpeningBalancePage: () =>
    '/accounting/payables/supplier-opening-balance',

  // ---------------------------------------------------------------------------
  // Module: Hành chính nhân sự (HR)
  // ---------------------------------------------------------------------------

  /** Hành chính nhân sự: Quản lý nhân sự */
  hrPersonnelPage: () => '/hr/personnel',

  /** Hành chính nhân sự: Quản lý chấm công */
  hrTimekeepingPage: () => '/hr/timekeeping',

  /** Hành chính nhân sự: Quản lý tài sản */
  hrAssetPage: () => '/hr/assets',

  /** Hành chính nhân sự: Phòng ban */
  hrDepartmentPage: () => '/hr/departments',

  // ---------------------------------------------------------------------------
  // Module: Tiện ích (UTILITY)
  // ---------------------------------------------------------------------------

  /** Lịch cơ quan — xem lịch làm việc, sự kiện của công ty / chi nhánh; companyCode được truyền qua query param */
  workSchedulePage: () => `/utility/work-schedule`,

  /** Tạo mới lịch cơ quan */
  workScheduleCreate: () => `/utility/work-schedule/create`,

  /** Chỉnh sửa lịch cơ quan theo mã lịch */
  workScheduleUpdate: (scheduleCode: string) =>
    `/utility/work-schedule/update/${scheduleCode}`,

  /** Danh bạ công ty — xem thông tin liên lạc toàn bộ nhân sự */
  contactPage: () => `/utility/contact`,
  newsListPage: () => `/utility/news`,

  /** Tài liệu cá nhân — quản lý thư mục / tài liệu của tôi và tài liệu được chia sẻ */
  myDocumentPage: () => `/utility/my-document`,

  /** Ký số điện tử — ký duyệt văn bản bằng chữ ký số (tab qua route param) */
  digitalSignaturePage: (tab?: DIGITAL_SIGN_TAB) =>
    `/utility/digital-signature${tab ? `/${tab}` : ''}`,

  /** Trang quản trị tiện ích — cấu hình lịch cơ quan, danh bạ… */
  utilityManagementPage: (type?: UTILITY_MANAGEMENT_TAB) =>
    `/utility/management/${type}`,

  // ---------------------------------------------------------------------------
  // Module: Thông báo (Notification) — dùng chung, không thuộc nhóm sidebar
  // ---------------------------------------------------------------------------

  /** Danh sách thông báo hệ thống của người dùng */
  notificationListPage: () => '/notifications',

  // ---------------------------------------------------------------------------
  // Module: Quản trị hệ thống (ADMIN_SYSTEM)
  // ---------------------------------------------------------------------------

  /** Trang phân quyền hệ thống — gán quyền cho người dùng / nhóm */
  managementPermission: () => `/management/acl-group`,

  /** Trang chi tiết nhóm quyền */
  aclGroupDetailPage: (code: string) => `/management/acl-group/${code}`,

  /** Trang quản lý tài khoản — thêm / sửa / khóa tài khoản người dùng */
  accountManagementPage: () => `/management/account`,

  /** Trang tài khoản ký số — khai báo tài khoản chứng thư số để ký số điện tử */
  signAccountManagementPage: () => `/management/sign-account`,

  /** Trang danh mục hệ thống — phòng ban, chức vụ, loại văn bản… */
  systemCategoryPage: (type?: SYSTEM_CATEGORY_TAB) =>
    `/management/category/${type}`,

  /** Trang quản lý module — bật / tắt các module trong hệ thống */
  moduleManagementPage: () => `/management/module`,

  // ---------------------------------------------------------------------------
  // Dev / Storybook — chỉ dùng cho môi trường phát triển
  // ---------------------------------------------------------------------------

  /** Trang thư viện icon — xem toàn bộ icon đang dùng trong dự án */
  iconGalleryPage: () => `/dev/icon-gallery`,
};

const pathsMap = {
  ...publicPaths,
  ...privatePaths,
};

export type TPathsMap = typeof pathsMap;

export const getPath = <TRoute extends keyof TPathsMap>(
  route: TRoute,
  ...params: Parameters<TPathsMap[TRoute]>
) => {
  const pathCb: (...args: any[]) => string = pathsMap[route];
  return pathCb(...params);
};
